import { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatDownArrowIcon } from 'shared/assets/svg/ChatDownArrowIcon';
import { FinecoPaperClipIcon } from 'shared/assets/svg/FinecoPaperClipIcon';
import SendIcon from 'shared/assets/svg/SendIcon';
import { Textarea } from 'shared/components/ui/textarea';
import activityPanelStore from 'shared/store/activityPanelStore';
import colorsStore from 'shared/store/colorsStore';
import { AssistantTaskUpdate } from 'types/AssistantTypes';
import './chat-input-styles.css';
import styles from './chatInput.module.css';
import { MicrophoneButton } from './speech-to-text/MicrophoneButton';

export type FileData = {
  name: string;
  mimeType: string;
  data: string;
};

export type MessageData = {
  text: string;
  file?: FileData;
};

type Props = {
  onSend?: (data: MessageData) => Promise<boolean>;
  onCancel?: () => void;
  disabled?: boolean;
  showArrow?: boolean;
  arrowAction?: () => void;
  taskUpdate?: AssistantTaskUpdate;
  /** Opens the portfolio document upload dialog; the paperclip shows only when set. */
  onAttach?: () => void;
};

export const ChatInput: React.FC<Props> = ({
  onSend,
  onCancel,
  disabled,
  showArrow,
  arrowAction,
  taskUpdate,
  onAttach,
}) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [recording, setRecording] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [file, setFile] = useState<FileData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { setUserSentFirstMessage } = activityPanelStore();

  useEffect(() => {
    if (textAreaRef) {
      adjustHeight(textAreaRef.current);
    }
  }, [textAreaRef, message]);

  useEffect(() => {
    if (recording) {
      setMessage('');
    }
  }, [recording]);

  useEffect(() => {
    if (!disabled && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [disabled]);

  const handleSend = () => {
    if (message) {
      const prevFileInputRefValue = fileInputRef.current?.value;

      setUserSentFirstMessage(true);
      onSend?.({
        text: message,
        file: file || undefined,
      }).then((success) => {
        if (success) return;

        if (!success) {
          setMessage(message);
          setFile(file);
          if (fileInputRef.current) {
            fileInputRef.current.value = prevFileInputRefValue;
          }
        }
      });

      setMessage('');
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // To prevent adding a new line to the end of a message
      handleSend();
    }
  };

  function adjustHeight(el: any) {
    if (!el) return;
    el.style.height = el.scrollHeight > el.clientHeight ? el.scrollHeight + 'px' : '46px';
  }

  let copyPlaceHolder = t('assistant.inputPlaceholder');
  if (taskUpdate) {
    if (taskUpdate.update) {
      // Build enhanced placeholder with progress and step info
      let enhancedPlaceholder = taskUpdate.update;

      // Add this back in later
      // if (taskUpdate.progress_percentage < 100) {
      //   // Add progress percentage if available
      //   if (taskUpdate.progress_percentage !== undefined) {
      //     enhancedPlaceholder += ` (${taskUpdate.progress_percentage}%)`;
      //   }
      //   // Add step information if available
      //   if (taskUpdate.detailed_message) {
      //     enhancedPlaceholder += ` - ${taskUpdate.detailed_message}`;
      //   }
      // } else if (taskUpdate.progress_percentage !== undefined) {
      //   enhancedPlaceholder += ` (${t('assistant.tasks.done')})`;
      // }

      copyPlaceHolder = enhancedPlaceholder;
    }
  } else {
    if (disabled) {
      copyPlaceHolder = t('assistant.inputDisabledPlaceholder');
      taskUpdate = { update: copyPlaceHolder, status: 'completed' };
    }
  }
  if (recording) {
    copyPlaceHolder = '';
  }

  // Custom placeholder style if the condition is met
  const customPlaceholderStyle = `
    textarea::placeholder {
      color: #00549F;
      font-size: 14px;
      font-weight: 600;
    }
  `;

  return (
    <div className="relative" ref={containerRef}>
      {showArrow && (
        <div
          onClick={arrowAction}
          className="-translate-x-1/2"
          style={{
            cursor: 'pointer',
            position: 'absolute',
            left: '50%',
            top: -55,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChatDownArrowIcon style={{ color: colorsConfig.finecoBluePrimary }} isExpanded={true} />
        </div>
      )}

      <div className="relative">
        <Textarea
          ref={textAreaRef}
          value={message}
          onKeyDownCapture={handleKeyDown}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={copyPlaceHolder}
          className={styles.chatTextInput}
          // Textarea sets its padding inline as a shorthand that changes with `disabled`; a
          // longhand paddingLeft is wiped on that update, so the whole shorthand goes here.
          style={onAttach ? { padding: '12px 40px 12px 80px' } : undefined}
          placeholderStyle={taskUpdate ? customPlaceholderStyle : undefined}
          disabled={disabled}
        />

        <div className="absolute right-4" style={{ right: 10, bottom: 14 }}>
          <div onClick={handleSend} className={message ? 'cursor-pointer duration-150 hover:opacity-50' : ''}>
            {!taskUpdate && (
              <SendIcon
                style={{
                  color: colorsConfig.finecoBluePrimary,
                  width: 18,
                  height: 18,
                }}
              />
            )}
            {taskUpdate && (
              <div onClick={onCancel} className={message ? 'cursor-pointer duration-150 hover:opacity-50' : ''}>
                <div className={styles.chatCancel}>
                  <CancelButton />
                </div>
              </div>
            )}
          </div>
        </div>

        {!taskUpdate && (
          <div className="absolute bottom-[14px] z-[45]" style={{ left: 10 }}>
            <MicrophoneButton
              onText={(t) => {
                onSend?.({ text: t, file: undefined });
              }}
              onRecordingChanged={setRecording}
              disabled={disabled}
            />
          </div>
        )}
        {onAttach && !taskUpdate && (
          <button
            type="button"
            className={`absolute bottom-[14px] z-[45] ${styles.attachButton}`}
            style={{ left: 44 }}
            onClick={onAttach}
            disabled={disabled}
            aria-label="Carica un documento di portafoglio"
            data-testid="doc-upload-paperclip"
            title="Carica un documento di portafoglio"
          >
            <FinecoPaperClipIcon fill={colorsConfig.finecoBluePrimary} />
          </button>
        )}
        {taskUpdate && (
          <div className="absolute bottom-[6px] z-50" style={{ left: 10 }}>
            {/* <Processing /> */}
          </div>
        )}
      </div>
    </div>
  );
};

const CancelButton = () => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  return (
    <div
      style={{
        width: '26px',
        height: '26px',
        border: '2px solid',
        borderColor: colorsConfig.finecoBluePrimary,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '12px',
          height: '12px',
          backgroundColor: colorsConfig.finecoBluePrimary,
        }}
      />
    </div>
  );
};
